import type { ApiRouterClient } from '@nly/api/orpc-router'
import type { ClientLink } from '@orpc/client'
import { createORPCClient, ORPCError } from '@orpc/client'
import { RPCLink } from '@orpc/client/fetch'

export type ApiClient = ApiRouterClient
export type ApiClientLink = ClientLink<Record<never, never>>
export type ApiClientHeaders = Headers | Record<string, string | string[] | undefined>

export type ApiClientUnauthorizedError = ORPCError<'UNAUTHORIZED', unknown>

export interface ApiClientObservers {
  onError?: (error: unknown) => void | Promise<void>
  onUnauthorized?: (error: ApiClientUnauthorizedError) => void | Promise<void>
}

export interface FetchApiClientOptions extends ApiClientObservers {
  credentials?: Request['credentials']
  fetch?: typeof fetch
  headers?: ApiClientHeaders | (() => ApiClientHeaders | Promise<ApiClientHeaders>)
  timeoutMs?: number
  url: string | URL
}

export type ApiClientTransportErrorCode
  = | 'NETWORK_ERROR'
    | 'REQUEST_ABORTED'
    | 'REQUEST_TIMEOUT'

export interface ApiClientErrorInfo {
  code: string
  detail?: string
  message: string
  requestId?: string
  status?: number
}

export const DEFAULT_API_TIMEOUT_MS = 60_000

const transportErrorMessages: Record<ApiClientTransportErrorCode, string> = {
  NETWORK_ERROR: 'Unable to reach the API',
  REQUEST_ABORTED: 'Request was cancelled',
  REQUEST_TIMEOUT: 'Request timed out',
}

export class ApiClientTransportError extends Error {
  readonly code: ApiClientTransportErrorCode

  constructor(code: ApiClientTransportErrorCode) {
    super(transportErrorMessages[code])
    this.name = 'ApiClientTransportError'
    this.code = code
  }
}

export function createApiClient(
  link: ApiClientLink,
  observers: ApiClientObservers = {},
): ApiClient {
  const observedLink: ApiClientLink = {
    async call(path, input, options) {
      try {
        return await link.call(path, input, options)
      }
      catch (cause) {
        const error = normalizeClientError(cause)
        if (isUnauthorizedError(error)) {
          await observers.onUnauthorized?.(error)
        }
        else {
          await observers.onError?.(error)
        }
        throw error
      }
    },
  }

  return createORPCClient<ApiClient>(observedLink)
}

export function createFetchApiClient(options: FetchApiClientOptions): ApiClient {
  const fetchImplementation = options.fetch ?? globalThis.fetch
  if (typeof fetchImplementation !== 'function') {
    throw new TypeError('A Fetch implementation is required')
  }

  const url = normalizeEndpointUrl(options.url)
  const link = new RPCLink<Record<never, never>>({
    url,
    headers: options.headers,
    fetch: createDeadlineFetch(
      fetchImplementation,
      options.timeoutMs ?? DEFAULT_API_TIMEOUT_MS,
      options.credentials ?? 'include',
    ),
  })

  return createApiClient(link, options)
}

export function getApiClientErrorInfo(error: unknown): ApiClientErrorInfo {
  if (error instanceof ORPCError) {
    const detail = getErrorDetail(error.data)
    return {
      ...(detail ? { detail } : {}),
      code: error.code,
      message: error.message,
      requestId: getRequestId(error.data),
      status: error.status,
    }
  }
  if (error instanceof ApiClientTransportError) {
    return {
      code: error.code,
      message: error.message,
    }
  }
  return {
    code: 'UNKNOWN_ERROR',
    message: 'API request failed',
  }
}

function normalizeEndpointUrl(value: string | URL) {
  const url = new URL(value)
  url.pathname = url.pathname.replace(/\/+$/, '')
  if (!url.pathname.endsWith('/app')) {
    throw new TypeError('API endpoint URL must end with /app')
  }
  return url
}

function normalizeClientError(error: unknown) {
  if (error instanceof ORPCError) {
    return error
  }

  let current: unknown = error
  const seen = new Set<unknown>()
  while (current instanceof Error && !seen.has(current)) {
    seen.add(current)
    if (current instanceof ApiClientTransportError) {
      return current
    }
    if (current.name === 'AbortError') {
      return new ApiClientTransportError('REQUEST_ABORTED')
    }
    current = current.cause
  }

  return new ApiClientTransportError('NETWORK_ERROR')
}

function createDeadlineFetch(
  fetchImplementation: typeof fetch,
  timeoutMs: number,
  credentials: Request['credentials'],
) {
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    throw new RangeError('timeoutMs must be a positive finite number')
  }

  return async (request: Request, init: { redirect?: Request['redirect'] }) => {
    const controller = new AbortController()
    const callerSignal = request.signal
    let timedOut = false
    let finished = false
    const forwardCallerAbort = () => controller.abort(callerSignal.reason)

    if (callerSignal.aborted) {
      forwardCallerAbort()
    }
    else {
      callerSignal.addEventListener('abort', forwardCallerAbort, { once: true })
    }

    const timeout = setTimeout(() => {
      timedOut = true
      controller.abort()
    }, timeoutMs)
    const finish = () => {
      if (finished) {
        return
      }
      finished = true
      clearTimeout(timeout)
      callerSignal.removeEventListener('abort', forwardCallerAbort)
    }

    try {
      const response = await fetchImplementation(
        new Request(request, { credentials, signal: controller.signal }),
        init,
      )
      return wrapResponseBody(response, finish, () => timedOut)
    }
    catch (error) {
      finish()
      if (timedOut) {
        throw new ApiClientTransportError('REQUEST_TIMEOUT')
      }
      throw error
    }
  }
}

const bodyReaderMethods = new Set<PropertyKey>([
  'arrayBuffer',
  'blob',
  'bytes',
  'formData',
  'json',
  'text',
])

function wrapResponseBody(
  response: Response,
  onComplete: () => void,
  hasTimedOut: () => boolean,
): Response {
  return new Proxy(response, {
    get(target, property) {
      if (bodyReaderMethods.has(property)) {
        const readBody = (Reflect.get(target, property, target) as (...args: unknown[]) => Promise<unknown>)
          .bind(target)
        return async (...args: unknown[]) => {
          try {
            return await readBody(...args)
          }
          catch (error) {
            if (hasTimedOut()) {
              throw new ApiClientTransportError('REQUEST_TIMEOUT')
            }
            throw error
          }
          finally {
            onComplete()
          }
        }
      }
      return Reflect.get(target, property, target)
    },
  })
}

function isUnauthorizedError(error: unknown): error is ApiClientUnauthorizedError {
  return error instanceof ORPCError && error.code === 'UNAUTHORIZED'
}

function getRequestId(data: unknown) {
  if (typeof data !== 'object' || data === null || !('requestId' in data)) {
    return undefined
  }
  return typeof data.requestId === 'string' ? data.requestId : undefined
}

function getErrorDetail(data: unknown) {
  if (typeof data !== 'object' || data === null || !('detail' in data)) {
    return undefined
  }
  return typeof data.detail === 'string' ? data.detail : undefined
}
